import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IconifyName } from '@/shared/components/iconify/register-icons';

import { Iconify } from '@/shared/components/iconify';

import {
  AccountSection,
  NotificationsSection,
  PreferencesSection,
  PrivacyDataSection,
} from '../pages/settings-sections';
import { ChangePasswordView } from './change-password-view';
import { SessionsView } from './sessions-view';

type SectionId = 'account' | 'preferences' | 'notifications' | 'privacy';
type SubView = 'change-password' | 'sessions';

interface TabEntry {
  id: SectionId;
  icon: IconifyName;
}

// Icons must be registered in shared/components/iconify/icon-sets.ts. Solar
// `*-bold` variants are what the rest of the settings surface already uses
// (see `change-password-view`'s `solar:settings-bold-duotone`), so we stay in
// the bold family for visual consistency.
const TABS: readonly TabEntry[] = [
  { id: 'account', icon: 'solar:users-group-rounded-bold' },
  { id: 'preferences', icon: 'solar:settings-bold-duotone' },
  { id: 'notifications', icon: 'solar:letter-bold' },
  { id: 'privacy', icon: 'solar:shield-check-bold' },
] as const;

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function SettingsDialog({ open, onClose }: SettingsDialogProps) {
  const { t } = useTranslation('settings');
  const theme = useTheme();
  const [active, setActive] = useState<SectionId>('account');
  const [subView, setSubView] = useState<SubView | null>(null);

  // New mobile-only drill-down state
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const [mobileShowSection, setMobileShowSection] = useState(false);

  // Reset to list when the dialog opens fresh
  useEffect(() => {
    if (open) {
      if (isMobile) {
        setMobileShowSection(false);
      }
      setSubView(null);
    }
  }, [open, isMobile]);

  const pickTab = (id: SectionId) => {
    setActive(id);
    setSubView(null);
    if (isMobile) {
      setMobileShowSection(true);
    }
  };

  const handleMobileBack = () => {
    if (subView) {
      setSubView(null);
    } else if (mobileShowSection) {
      setMobileShowSection(false);
    } else {
      onClose();
    }
  };

  const renderContent = () => {
    // Pass isMobile to sub-views so they can hide their own breadcrumbs
    if (subView === 'change-password') {
      return <ChangePasswordView onBack={() => setSubView(null)} isMobile={isMobile} />;
    }
    if (subView === 'sessions') {
      return <SessionsView onBack={() => setSubView(null)} isMobile={isMobile} />;
    }
    // Pass isMobile to sections so they can hide their main heading
    switch (active) {
      case 'account':
        return (
          <AccountSection
            isMobile={isMobile}
            onChangePassword={() => setSubView('change-password')}
            onManageSessions={() => setSubView('sessions')}
          />
        );
      case 'preferences':
        return <PreferencesSection isMobile={isMobile} />;
      case 'notifications':
        return <NotificationsSection isMobile={isMobile} />;
      case 'privacy':
        return <PrivacyDataSection isMobile={isMobile} />;
    }
  };

  const subViewTitleMap: Record<SubView, string> = {
    'change-password': t('account.changePassword.title'),
    sessions: t('account.sessions.title'),
  };

  const headerTitle = subView
    ? subViewTitleMap[subView]
    : mobileShowSection
      ? t(`tabs.${active}`)
      : t('navTitle');

  const desktopNav = (
    <Box
      component="nav"
      aria-label={t('navTitle')}
      sx={{
        borderInlineEnd: 1,
        borderColor: 'divider',
        p: 2.5,
        backgroundColor: 'background.default',
        overflowY: 'auto',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Cairo Variable", sans-serif',
          fontSize: 12,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'text.disabled',
          fontWeight: 600,
          mb: 1.5,
          paddingInlineStart: 1.5,
        }}
      >
        {t('navTitle')}
      </Typography>
      <Tabs
        orientation="vertical"
        value={active}
        onChange={(_e, newValue) => pickTab(newValue)}
        aria-label={t('navTitle')}
        sx={{
          '& .MuiTabs-indicator': {
            width: 3.5,
            backgroundColor: 'primary.main',
            // Use logical start-corner radii; the indicator sits on the start
            // edge of the active tab in both LTR (left) and RTL (right). The
            // transform above flips the indicator's horizontal anchor for RTL.
            borderStartStartRadius: 3,
            borderEndStartRadius: 3,
            transform: theme.direction === 'rtl' ? 'scaleX(-1)' : 'none',
          },
        }}
      >
        {TABS.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            label={t(`tabs.${tab.id}`)}
            icon={<Iconify icon={tab.icon} width={20} />}
            iconPosition="start"
            sx={(theme) => ({
              minHeight: 48,
              justifyContent: 'flex-start',
              fontWeight: 500,
              fontSize: 13.5,
              textTransform: 'none',
              borderRadius: (t) => `${t.shape.borderRadius}px`,
              color: 'text.secondary',
              px: 1.5,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: 'text.primary',
              },
              '&.Mui-selected': {
                fontWeight: 600,
                color:
                  theme.palette.mode === 'dark' ? theme.palette.primary.light : 'primary.dark',
                backgroundColor: `${theme.palette.primary.main}14`,
              },
              '&.Mui-selected .MuiIcon-root': {
                color: 'primary.main',
              },
              '& .MuiIcon-root': {
                color: 'text.disabled',
                minWidth: 'auto',
                marginInlineEnd: 1.25,
              },
            })}
          />
        ))}
      </Tabs>
    </Box>
  );

  const mobileNav = (
    <List sx={{ pt: 0, backgroundColor: 'background.default', height: '100%' }}>
      {TABS.map((tab) => (
        <ListItemButton key={tab.id} onClick={() => pickTab(tab.id)} sx={{ py: 1.5, px: 2.5 }}>
          <ListItemIcon sx={{ minWidth: 'auto', mr: 2, color: 'text.secondary' }}>
            <Iconify icon={tab.icon} width={22} />
          </ListItemIcon>
          <ListItemText
            primary={t(`tabs.${tab.id}`)}
            primaryTypographyProps={{ fontSize: 15, fontWeight: 500 }}
          />
          <Box
            sx={{
              color: 'text.disabled',
              transform: theme.direction === 'rtl' ? 'scaleX(-1)' : 'none',
            }}
          >
            <Iconify icon="eva:arrow-ios-forward-fill" width={18} />
          </Box>
        </ListItemButton>
      ))}
    </List>
  );

  const mobileHeader = (
    <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Toolbar>
        <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-start' }}>
          {(mobileShowSection || subView) && (
            <IconButton size="small" onClick={handleMobileBack} aria-label={t('back')}>
              {/* No dedicated `back-fill` in the icon set — reuse `forward-fill`
                  and flip it to point at the start edge. The mirror is the
                  opposite of normal: LTR back points left (scaleX(-1)), RTL
                  back points right (identity). */}
              <Iconify
                icon="eva:arrow-ios-forward-fill"
                width={22}
                sx={(t) => ({ transform: t.direction === 'rtl' ? 'none' : 'scaleX(-1)' })}
              />
            </IconButton>
          )}
        </Box>
        <Typography sx={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 600 }}>
          {headerTitle}
        </Typography>
        <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={onClose} aria-label={t('close')}>
            <Iconify icon="mingcute:close-line" width={20} />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            height: { xs: '100dvh', md: '85vh' },
            maxHeight: { xs: '100dvh', md: 880 },
            borderRadius: { xs: 0, md: `${theme.shape.borderRadius}px` },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {isMobile ? (
        <>
          {mobileHeader}
          {mobileShowSection || subView ? (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>{renderContent()}</Box>
          ) : (
            mobileNav
          )}
        </>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '240px 1fr',
            height: '100%',
            minHeight: 0,
          }}
        >
          {desktopNav}
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <IconButton size="small" onClick={onClose} aria-label={t('close')}>
                <Iconify icon="mingcute:close-line" width={18} />
              </IconButton>
            </Stack>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>{renderContent()}</Box>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
